<template>
  <div>
    <h1>Zamówienie o id {{ data.id }}</h1>

    <h2>Parametry zamówienia:</h2>

    <v-row>
      <v-col class="py-3" md="4" cols="12">
        <v-text-field filled label="Nazwa firmy" readonly v-model="data.contractor_name" />
        <v-text-field filled label="Nip" readonly v-model="data.contractor_tin" />
      </v-col>
      <v-col class="px-3" md="4" cols="12">
        <v-textarea :value="address" filled class="input-capitalize" height="144px" label="Adres" no-resize readonly hide-details />
      </v-col>
      <v-col md="4" cols="12">
        <v-textarea label="Komentarz" v-model="data.service_order_comment" outlined filled />
      </v-col>
      <v-col md="4" cols="12">
        <v-text-field
          readonly
          :value="data.order_group_id"
          filled
          label="Id grupy zamówienia"
          append-outer-icon="fa-copy"
          @click:append-outer="$copyText(data.order_group_id)"
        />
      </v-col>
      <v-col class="px-2" md="4" cols="12" v-if="!!data.project_name && data.project_name.length > 0">
        <v-text-field
          readonly
          :value="data.project_name"
          filled
          label="Atrybut projekt"
          append-outer-icon="fa-copy"
          @click:append-outer="$copyText(data.project_name)"
        />
      </v-col>
      <v-col class="px-2" md="4" cols="12" v-if="!!data.order_number && data.order_number.length > 0">
        <v-text-field
          readonly
          :value="data.order_number"
          filled
          label="Atrybut numer zamówienia"
          append-outer-icon="fa-copy"
          @click:append-outer="$copyText(data.order_number)"
        />
      </v-col>
      <v-col class="px-2" md="4" cols="12" v-if="!!data.external_customer_name && data.external_customer_name.length > 0">
        <v-text-field
          readonly
          :value="data.external_customer_name"
          filled
          label="Atrybut klient"
          append-outer-icon="fa-copy"
          @click:append-outer="$copyText(data.external_customer_name)"
        />
      </v-col>
    </v-row>

    <admin-travel-service-additional-params-preview :additional-parameters="data.additional_parameters"> </admin-travel-service-additional-params-preview>

    <p style="font-size: larger">
      <span v-if="data.phone_numbers != null && data.phone_numbers.length > 3"> Telefon: {{ data.phone_numbers }}<br /></span>
      <span v-if="data.contractor_mail_addresses != null && data.contractor_mail_addresses.length > 3">
        Email firmowy:
        <a :href="'mailto:' + data.contractor_mail_addresses">{{ data.contractor_mail_addresses }}</a
        ><br
      /></span>
      Utworzono: {{ data.created_at }} przez
      <span class="font-weight-bold">{{ data.created_by_name }} </span>
      <span v-if="data.created_by_email != null && data.created_by_email.length > 0">
        email zamawiającego:
        <a :href="'mailto:' + data.created_by_email">{{ data.created_by_email }}</a></span
      >
      <span v-if="data.created_by_phone != null && data.created_by_phone.length > 0"
        >, nr telefonu: <span class="font-weight-bold">{{ data.created_by_phone }}</span></span
      >
      .
      <br />
      Status zamówienia:
      <order-chip :status="data.status" />
    </p>

    <h2>Lista zamówionych usług:</h2>

    <v-data-table
      :headers="headers"
      :items="data.items"
      hide-default-footer
      :loading="$store.getters['Ajax/loading'] ? 'accent' : false"
      :footer-props="{ 'items-per-page-options': [{ text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }] }"
    >
      <template #item="props">
        <tr>
          <td>{{ $t("travelService.types." + props.item.type) }}</td>
          <td>
            <span v-if="props.item.type === 'hotel'">
              Od {{ props.item.start_date }} {{ props.item.start_time }} do
              {{ props.item.end_date }}
            </span>
            <span v-if="props.item.type === 'flight'">
              Wylot {{ props.item.start_date }} {{ props.item.start_time }}
              <span v-if="props.item.return_date != null">Lot powrotny {{ props.item.return_date }} {{ props.item.return_time }}</span>
            </span>
            <span v-if="props.item.type === 'rail'">
              Wyjazd {{ props.item.start_date }} {{ props.item.start_time }}
              <span v-if="props.item.return_date != null">Powrót {{ props.item.return_date }} {{ props.item.return_time }}</span>
            </span>
            <span v-if="props.item.type === 'car'">
              Od {{ props.item.start_date }} {{ props.item.start_time }} do {{ props.item.end_date }} {{ props.item.end_time }}
            </span>
            <span v-if="props.item.type === 'insurance'"> Od {{ props.item.start_date }} do {{ props.item.end_date }} </span>
          </td>
          <td>
            <span v-if="props.item.type === 'hotel'">
              {{ allRoomTypes().find(v => v.value === props.item.room_type).text }}
              <br />
              <b>{{ props.item.locationText }}</b>
            </span>
            <span v-if="props.item.type === 'flight'">
              Z <b>{{ props.item.startLocationText }}</b> do <b>{{ props.item.endLocationText }}</b> <br />
              Klasa:
              <b>{{ allFlightClassTypes().find(v => v.value === props.item.seat_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'rail'">
              Z <b>{{ props.item.startLocationText }}</b> do <b>{{ props.item.endLocationText }}</b> <br />
              Klasa:
              <b>{{ allRailClassTypes().find(v => v.value === props.item.seat_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'car'">
              Odbiór <b>{{ props.item.startLocationText }}</b>
              <span v-if="props.item.endLocationText != null && props.item.endLocationText.length > 0">
                zwrot <b>{{ props.item.endLocationText }}</b></span
              >
              <br />
              Klasa pojazdu:
              <b>{{ allCarTypes().find(v => v.value === props.item.car_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'other'">
              Usługa: <b>{{ props.item.name }}</b>
            </span>
            <span v-if="props.item.type === 'insurance'">
              Lokalizacja: <b>{{ props.item.locationText }}</b>
            </span>
            {{ props.item.comment }} <br />
            Dla:
            <span v-for="user in props.item.users" :key="user.passenger_id">
              <b>{{ user.name }} {{ user.surname }}</b
              >,
            </span>
            <template v-if="!!props.item.cost_centre">
              <br />
              MPK: <b>{{ props.item.cost_centre }}</b>
            </template>
          </td>
          <td>
            <single-service-order-chip :status="props.item.status"> </single-service-order-chip>
          </td>
          <td>
            <v-tooltip
              bottom
              v-if="
                [
                  itemsStatus.STATUS_CREATED,
                  itemsStatus.STATUS_ALREADY_SEND,
                  itemsStatus.STATUS_READY_TO_SEND,
                  itemsStatus.STATUS_REQUEST_CLIENT_ANSWER_FOR_OFFER
                ].includes(props.item.status)
              "
            >
              <template #activator="{on}">
                <v-btn :to="'/admin/travel-service/item/edit/' + props.item.id" icon v-on="on">
                  <v-icon>assignment</v-icon>
                </v-btn>
              </template>
              Zaofertuj zamówienie
            </v-tooltip>
            <v-tooltip
              bottom
              v-if="
                [
                  itemsStatus.STATUS_CANCELED_BY_CLIENT,
                  itemsStatus.STATUS_BOOKING_CANCELLED,
                  itemsStatus.STATUS_OFFER_SELECTED_BY_CLIENT,
                  itemsStatus.STATUS_CLIENT_REQUEST_BOOKING_CANCEL
                ].includes(props.item.status)
              "
            >
              <template #activator="{on}">
                <v-btn :to="'/admin/travel-service/item/readonly/' + props.item.id" icon v-on="on">
                  <v-icon>assignment</v-icon>
                </v-btn>
              </template>
              Podgląd oferty
            </v-tooltip>
            <v-tooltip
              bottom
              v-if="
                [itemsStatus.STATUS_OFFER_ACCEPTED_BY_CLIENT, itemsStatus.STATUS_BOOKED, itemsStatus.STATUS_REQUEST_CLIENT_ANSWER_FOR_BOOK].includes(
                  props.item.status
                )
              "
            >
              <template #activator="{on}">
                <v-btn :to="'/admin/travel-service/add-booking/' + props.item.id" icon v-on="on">
                  <v-icon>assignment</v-icon>
                </v-btn>
              </template>
              Dołącz rezerwację
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import SingleServiceOrderChip from "../../../components/TravelService/SingleServiceOrderChip";
import { itemsFilterStatus, itemsStatus, serviceStatus, singleServiceOrderStatus } from "../../../staticData/ServiceOrder";
import { allCarTypes, allFlightClassTypes, allRailClassTypes, allRoomTypes } from "../../../staticData/TravelServiceTypes";
import OrderChip from "../../../components/TravelService/OrderChip";
import AdminTravelServiceAdditionalParamsPreview from "../../../components/TravelService/AdminTravelServiceAdditionalParamsPreview";

export default {
  name: "AdminTravelServiceEditView",
  components: {
    AdminTravelServiceAdditionalParamsPreview,
    OrderChip,
    SingleServiceOrderChip
  },
  data: () => ({
    headers: [
      { text: "Rodzaj usługi", value: "type", sortable: false },
      { text: "Termin", value: "date", sortable: false },
      { text: "Szczegóły", value: "param", sortable: false },
      { text: "Status", value: "status" },
      { text: "", value: "actions", sortable: false }
    ],
    data: {
      additional_parameters: [],
      id: null,
      company: "",
      contractor_name: "",
      contractor_tin: "",
      contractor_country: "",
      contractor_short_name: "",
      contractor_region: "",
      contractor_city: "",
      contractor_street_and_number: "",
      contractor_postal_code: "",
      contractor_mail_addresses: "",
      phone_numbers: null,
      created_by_name: null,
      created_at: null,
      service_order_comment: null,
      status: null
    },
    singleServiceOrderStatus: singleServiceOrderStatus,
    serviceStatus: serviceStatus,
    itemsStatus: itemsStatus
  }),
  computed: {
    itemsFilterStatus,
    address() {
      let address = "";
      if (this.data.contractor_street_and_number != null) {
        address += this.data.contractor_street_and_number.toLowerCase() + "\r\n";
      }
      if (this.data.contractor_postal_code != null && this.data.contractor_city != null) {
        address += this.data.contractor_postal_code.toLowerCase() + " " + this.data.contractor_city.toLowerCase() + "\r\n";
      }
      if (this.data.contractor_country != null) {
        address += this.data.contractor_country.toLowerCase();
      }
      return address;
    }
  },
  created() {
    this.axios.get("/admin/travel-service-admin/get-service-order?id=" + this.$route.params.id).then(resp => {
      this.data = resp.data;
    });
  },
  methods: {
    allCarTypes,
    allFlightClassTypes,
    allRailClassTypes,
    allRoomTypes
  }
};
</script>

<style></style>
